/** Longer than any auditor function's timeout, so an older marker belongs to a run that died. */
export const MAX_AUDIT_RUN_AGE_MS = 120000;

export function isStoredAuditRunInFlight(sessionActivity, now = Date.now()) {
  const startedAt = sessionActivity?.auditMeta?.runStartedAt;
  const startedAtMillis = typeof startedAt?.toMillis === 'function' ? startedAt.toMillis() : null;

  return Number.isFinite(startedAtMillis) && Math.abs(now - startedAtMillis) < MAX_AUDIT_RUN_AGE_MS;
} // isStoredAuditRunInFlight()

/** Every stored run counts, whoever made it. An audit older than the tally counts as none. */
export function getStoredAuditRunCount(sessionActivity) {
  return sessionActivity?.auditMeta?.auditRunCount ?? 0;
} // getStoredAuditRunCount()

/**
 * True once someone else's run replaced the stored audit this record knows, e.g. the re-audit at
 * signing. Compared by count, not id: the activity lags behind this client's own run. A run in
 * flight never counts, as its own result may reach the activity first.
 *
 * @param {Object} auditRun - useAuditRunState()'s record for this note.
 */
export function isLocalAuditRunSuperseded(auditRun, sessionActivity) {
  const knownAuditRunCount = auditRun?.auditRunCount ?? auditRun?.baselineAuditRunCount ?? null;

  return !auditRun?.isRunning
    && knownAuditRunCount !== null
    && getStoredAuditRunCount(sessionActivity) > knownAuditRunCount;
} // isLocalAuditRunSuperseded()

/**
 * False once the note is submitted: the server may re-audit it, and only the document hears of that.
 * Reopening brings the status back, so a re-audit that already landed is told by the count instead.
 *
 * @param {Object} auditRun - useAuditRunState()'s record for this note.
 */
export function isLocalAuditRunCurrent(auditRun, sessionActivity) {
  return !!auditRun?.attemptCount
    && auditRun.status === sessionActivity?.status
    && !isLocalAuditRunSuperseded(auditRun, sessionActivity);
} // isLocalAuditRunCurrent()

/**
 * A client that ran the audit itself is handed the result before the marker's removal reaches it,
 * so its own answer wins and the marker only fills the gap.
 *
 * @param {Object} auditRun - useAuditRunState()'s record for this note.
 */
export function isAuditRunInFlight(auditRun, sessionActivity, now = Date.now()) {
  return isLocalAuditRunCurrent(auditRun, sessionActivity)
    ? auditRun.isRunning
    : isStoredAuditRunInFlight(sessionActivity, now);
} // isAuditRunInFlight()
